import { UploadFile } from 'components/Links/Creators/LinkCreatorDocumentLibrary';
import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotConversation,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotChatInputUsageProps,
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelRuntime,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { TenantProcessStateLibrary } from 'models/copilot/Processes/Library/tenantProcessStateLibrary';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import { apiCopilotExecuteProcessLibrary, apiCopilotUploadFileToStorage } from 'services/Api/copilotService';
import { getSceneHelpBinding, getTasksFromReportBinding } from '../Generic/genericSceneBindings';
import { CopilotLibraryPanelState } from './copilotLibraryPanelState';
import { librarySummarizeDocumentTemplate } from './libraryAIActionTemplates';

export class CopilotLibraryPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.Library;
    this.name = 'Library';
    this.supportsChat = true;
    this.supportsAttachments = true;
    this.requiresActiveAction = true;
  }

  // the help question first, then the process start and the report action. The document question is always
  // enabled: the panel computes its actions once per scene, so a rule on the selection would go stale. At send time
  // the selected document becomes the turn's document; without one the prompt asks for an upload.
  getBindings(_content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    return [
      getSceneHelpBinding(),
      createAIActionBinding(processStartTemplate, { processId: TenantProcess.Library }),
      getTasksFromReportBinding(),
      createAIActionBinding(librarySummarizeDocumentTemplate, undefined, { usesSelectedDocument: true }),
    ];
  }

  async getAnswer(runtime: CopilotPanelRuntime, t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (action?.processId !== TenantProcess.Library) return;
    const state = this.getState(runtime);
    if (!state) return;

    runtime.setIsLoading(true);
    const fileLinks = await this.uploadFiles(runtime, state.attachedFiles, t);
    const links = [
      ...state.attachedLinks.map((link) => link.clone()),
      ...fileLinks,
      ...(state.libraryState?.links ?? []),
    ];
    const lists = runtime.targetFolder ? [runtime.targetFolder] : state.libraryState?.lists ?? [];
    state.clearAttachments();
    const answer = await apiCopilotExecuteProcessLibrary(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      links,
      lists,
      state.libraryState?.controlLinks ?? [],
      runtime.connectionId,
      runtime.accessToken,
      runtime.appContext.globalDataCache,
    );
    if (!answer) return;

    if (action.status !== TenantProcessStatus.Completed) state.setLibraryState(answer);
    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
    if (answer.links && answer.links.length > 0) {
      if (action.status === TenantProcessStatus.Completed) {
        runtime.resultBus.publishLibraryOrganization(answer);
      } else {
        state.setLibraryStateDisplay(answer);
        runtime.setPreviewContent(CopilotPreviewContent.Usage);
      }
    }
  }

  getChatInputUsageProps(runtime: CopilotPanelRuntime): CopilotChatInputUsageProps {
    const state = this.getState(runtime);

    return {
      attachmentMode: this.isAttachmentMode(runtime),
      canSubmit: state?.hasAttachments === true,
      hasAttachments: state?.hasAttachments ?? false,
      targetFolder: state?.targetFolder,
      setShowAddDocumentModal: state?.setShowAddDocumentModal,
      setShowFolderPicker: state?.setShowSelectDriveModal,
      setShowFileUploader: state?.setShowUploadModal,
    };
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!this.isAvailableForContent(content)) return undefined;

    return { entityType: EntityTypes.NotSet, entityId: 0 };
  }

  prepareQuestion(question: string, runtime: CopilotPanelRuntime, t: TFunction<string[]>): string {
    const state = this.getState(runtime);
    if (!state?.hasAttachments) return question;

    return `${question}\n ${t('copilot:Panel.Attachments.QuestionWithAttachments', {
      links: state.attachedLinks.length,
      files: state.attachedFiles.length,
    })}`;
  }

  getWelcomeMessages(action: CopilotAction, t: TFunction<string[]>): string[] {
    if (action.processId !== TenantProcess.Library) return [];

    return [t('copilot:Processes.Library.Welcome'), t('copilot:Processes.Library.WelcomeSharePointFolders')];
  }

  getCreatePreviewText(t: TFunction<string[]>): string | undefined {
    return t('copilot:Actions.Library.Create');
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    const panelState = this.getState(runtime);
    if (!(state.data instanceof TenantProcessStateLibrary) || !panelState) {
      return super.applyProcessState(runtime, state);
    }

    panelState.setLibraryState(state.data);
    panelState.setLibraryStateDisplay(state.data);
    runtime.setConversation(state.conversation);

    return state.data.links.length > 0 ? CopilotPreviewContent.Usage : undefined;
  }

  clearPreview(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    state?.setLibraryState(undefined);
    state?.setLibraryStateDisplay(undefined);
  }

  saveDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setLibraryState(state.libraryStateDisplay);
  }

  discardDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setLibraryStateDisplay(state.libraryState);
  }

  cleanUp(runtime: CopilotPanelRuntime): void {
    this.getState(runtime)?.clearAttachments();
  }

  private async uploadFiles(
    runtime: CopilotPanelRuntime,
    files: UploadFile[],
    t: TFunction<string[]>,
  ): Promise<ResourceLink[]> {
    if (files.length === 0) return [];

    const newLinks = [] as ResourceLink[];
    let failedUploads = 0;
    for (const file of files) {
      try {
        runtime.setNewStatus(t('copilot:StatusMessage.Uploading', { count: newLinks.length + 1, total: files.length }));
        const uploadedFile = await apiCopilotUploadFileToStorage(file.file, runtime.accessToken);
        if (!uploadedFile) continue;
        const state = this.getState(runtime);
        const fileName = state?.selectedFileNames[file.id] ?? file.file.name;
        const newLink = new ResourceLink();
        newLink.linkName = fileName;
        newLink.linkURL = uploadedFile;
        newLinks.push(newLink);
      } catch {
        failedUploads++;
      }
    }
    if (failedUploads > 0) {
      const message = new CopilotConversationItem();
      message.type = CopilotConversionItemType.Copilot;
      message.response = t('copilot:Processes.Library.UploadFails', { count: failedUploads });
      const newConversation = runtime.conversation ? runtime.conversation.clone() : new CopilotConversation();
      newConversation.thread.push(message);
      runtime.setConversation(newConversation);
    }

    return newLinks;
  }

  private getState(runtime: CopilotPanelRuntime): CopilotLibraryPanelState | undefined {
    return runtime.getUsageState<CopilotLibraryPanelState>(this.context);
  }
}
